import type { Messages } from './de';

/** Englisch — zugleich der Rückfall, wenn das Gerät weder Deutsch noch Französisch spricht. */
export const en: Messages = {
  label: 'English',
  tag: 'en',

  format: {
    decimal: '.',
  },

  start: {
    resumeKicker: 'Assessment in progress',
    resumeTitle: 'Continue?',
    resumeBody: (candidate: string, station: string) =>
      `There is an unfinished assessment for ${candidate} at ${station}.`,
    resumeKeep: 'Continue',
    resumeFresh: 'Start over',
    resumeNote: 'Starting over deletes nothing — the assessment stays saved.',
    pickKicker: 'Candidate',
    pickTitle: 'Who is being assessed?',
    scan: 'Scan QR code',
    fromList: 'Choose from list',
    startStation: 'Start station',
  },

  backlog: {
    title: 'Not yet transferred',
    packets: 'Packets',
    files: 'Files',
    rejected: 'given up',
    kinds: {
      pakete: 'Marks (log entries)',
      screenshot: 'Screenshots of the sheet',
      photo: 'Photos',
      video: 'Videos',
      audio: 'Audio recordings',
      handwriting: 'Handwritten notes',
      annotation: 'Annotations',
      sonstige: 'Other files',
    },
    rejectedRow: 'Of which given up',
    autoOff: 'Automatic upload is off. Data is only sent via “Send now”.',
    label: (pakete: number, dateien: number) =>
      `Not yet transferred: ${pakete} packets, ${dateien} files`,
    state: {
      leer: 'Everything transferred.',
      wartet: 'Waiting for the next attempt. The app retries on its own.',
      sendet: 'Transferring …',
      steckt: 'Some items have not gone through yet and are waiting here. The assessments are saved.',
    },
  },
  header: {
    stationPrefix: 'Station',
    candidate: 'Candidate',
    /** Steht im Kopf, solange der Ausweis noch nicht gescannt ist. */
    noCandidate: 'no candidate',
    remaining: 'Time left',
    total: 'Total',
    settings: 'Settings',
    chooseExam: 'Choose exam and station',
    startClock: 'Resume clock',
    pauseClock: 'Pause clock',
    resetClock: 'Reset time',
    setClock: 'Set station time',
    problemExam: 'Exam changed or removed on the server',
  },

  bogen: {
    vignette: 'Case vignette',
    open: 'Open',
    task: 'Task',
    comment: 'Comment',
    comments: (n) => (n === 1 ? '1 comment' : `${n} comments`),
    fullScore: 'full marks',
    noScore: 'no marks',
    reduced: 'reduced',
    adjusted: 'adjusted',
    pending: 'not yet set',
    possible: (points) => `${points} pts possible`,
    ofPoints: (value, max) => `${value} / ${max} pts`,
    points: (value) => `${value} pts`,
    progress: (done, total) => `${done} of ${total} tasks assessed`,
    commentsOn: (tasks) => `Comments on task ${tasks}`,
    submit: 'Submit assessment',
    stationComment: 'Station comment',
    stationCommentOn: (n: number) => `Station comment · ${n}`,
    submitArm: 'Tap again to submit',
    submitted: 'Submitted',
    emptyTitle: 'No sheet loaded',
    emptyNote:
      'There is no assessment sheet for this station. Choose another station or load the exam again.',
    chooseStation: 'Choose station',
  },

  score: {
    upTo: (max) => `up to ${max} points`,
    undoOverride: 'Undo manual score — calculate again',
    criterionOf: (index, total) => `Criterion ${index} of ${total}`,
    clearChoice: 'Clear choice',
  },

  aufsicht: {
    kicker: 'Exam supervision',
    titel: 'Message',
    gelesen: 'Got it',
    zugabe: (min: number, sek: number) =>
      `Exam supervision: +${[min > 0 ? `${min} min` : '', sek > 0 ? `${sek} s` : ''].filter(Boolean).join(' ')} station time`,
  },
  timer: {
    title: 'Station time',
    running: 'running',
    paused: 'paused',
    expired: 'expired',
    resume: 'Resume',
    pause: 'Pause',
    reset: 'Reset',
    minutes: (n) => `${n} min`,
    fromStation: 'Station',
    hide: 'Hide time left',
  },


  unbekannteEinstellungen: (namen: string) => `Unknown settings ignored: ${namen}`,
  aufnahmeSymbol: 'Screenshot taken for the exam administrators',
  preview: {
    laedt: 'Loading preview …',
    notFound: 'This preview no longer exists — already collected or expired. Create it again in Relay.',
    network: 'No connection to the preview service. Check the network and scan again.',
    clock: 'This device’s clock is wrong. Set the time and scan again.',
    signature: 'The preview service rejected this device.',
    decrypt: 'The preview could not be decrypted.',
    version: 'This preview comes from a newer version. Please update the app.',
    tooLarge: 'The preview is too large for this device.',
    format: 'The preview is incomplete or damaged.',
    realExam: 'This exam is already on the device as a real exam. The preview does not overwrite it.',
    unbekannt: 'The preview could not be opened.',
  },
  qr: {
    kicker: 'QR code',
    title: 'Apply settings?',
    scan: 'Scan QR code',
    scanLang: 'Scan settings or selection',
    scanHinweis: 'Setup or selection',
    uebernimmt: (n: number) => (n === 1 ? 'One setting will be applied:' : `${n} settings will be applied:`),
    zugang: 'Server access',
    uebrige: 'Other settings',
    warnung: 'A scanned code takes precedence over the device management profile. It carries the server credentials in clear text — a printout does not belong on a notice board.',
    ignoriert: (n: number) => (n === 1 ? 'One entry without a counterpart' : `${n} entries without a counterpart`),
    nichts: 'No setting can be taken from this code.',
    uebernehmen: 'Apply',
    abbrechen: 'Cancel',
    gesetzt: 'Settings applied.',
    auswahlGesetzt: 'Selection applied.',
    examFehlt: 'The exam from the code is not on this device. Download it first, then scan again.',
    stationFehlt: 'The station, examiner or actor from the code does not exist in this exam.',
    fremd: 'This QR code does not belong to fairexa Stage.',
    kaputt: 'The QR code is damaged or incomplete.',
    fassung: 'This code comes from a newer version of the app.',
    geraeteart: 'This code is meant for a different device type.',
    leer: '(empty)',
    gruppe: (name: string) => `Group: ${name}`,
    verwerfen: 'Discard scanned settings',
    namen: {
      server_url: 'Server address',
      classic_login: 'User name',
      classic_password: 'Password',
      server_protocol: 'Protocol',
      settings_password: 'Settings password',
      language: 'Language',
      sheet_half_points: 'Half points',
      sheet_show_timer: 'Station clock',
      sheet_show_actors: 'Actors',
      save_photos_in_album: 'Copy to Photos',
      min_brightness: 'Brightness, darkest level',
    } as Partial<Record<string, string>>,
  },
  setup: {
    presenceLoaded: 'downloaded',
    presenceServer: 'available',
    presenceLocal: 'no longer on the server',
    warnStale: 'Changed on the server — to apply it, delete and download again. Recorded ratings are lost in the process.',
    simulativ: 'Simulation exam',
    simulativNote: 'Nothing is recorded — no rating, no comment, no recording leaves this device.',
    vorschau: 'Preview',
    vorschauNote: 'From Relay, to try out. Nothing is sent. Delete it by hand when done.',
    warnGone: 'The server no longer knows this exam. Ratings can no longer be transferred.',
    showAll: 'Show all exams',
    download: 'Download',
    downloading: 'Downloading…',
    deleteExam: 'Delete from this device',
    pruefungsEinstellungen: 'Settings of this exam',
    pruefungsEinstellungenNote: 'Apply while this exam is selected:',
    einstellungAn: 'on',
    einstellungAus: 'off',
    deleteArm: 'Tap again to delete',
    deleteTitle: 'Delete exam?',
    deleteBody: (name: string) => `"${name}" will be removed from this device completely.`,
    deleteRatings: (candidates: number, pending: number) =>
      `It holds assessments for ${candidates} ${candidates === 1 ? 'candidate' : 'candidates'}` +
      (pending > 0
        ? ` — ${pending} ${pending === 1 ? 'entry has' : 'entries have'} not been transferred yet and will be lost for good.`
        : ' — all of them have been transferred.'),
    deleteConfirm: 'Delete permanently',
    deleteArchiveNote: 'The recordings stay on the device for now and are deleted for good once the set period has passed.',
    deleteCancel: 'Cancel',
    downloadFailed: 'Download failed',
    refreshFailed: 'The server cannot be reached',
    chooseTitle: 'Set up exam',
    kicker: 'Set up exam',
    exam: 'Exam',
    pickTitle: 'Exam selection',
    refresh: 'Refresh',
    loaded: 'loaded',
    notLoaded: 'not loaded',
    station: 'Station',
    stations: (n) => (n === 1 ? '1 station' : `${n} stations`),
    examiner: 'Examiner',
    examiners: (n) => (n === 1 ? '1 examiner' : `${n} examiners`),
    actor: 'Simulated patient',
    actors: (n) => (n === 1 ? '1 simulated patient' : `${n} simulated patients`),
    close: 'Close',
    apply: 'Apply',
  },

  candidates: {
    kicker: 'Change candidate',
    summary: (total, rated) => `${total} candidates · ${rated} assessed`,
    search: 'Name, student or exam no.',
    searchLabel: 'Search candidate',
    sortBy: 'Sort by',
    sort: {
      nachname: 'Surname',
      vorname: 'First name',
      matrikel: 'Student no.',
      pruefungsnr: 'Exam no.',
    },
    filterAll: 'All',
    offeneAufgaben: (n: number) => (n === 1 ? '1 open' : `${n} open`),
    komplett: 'complete',
    filterOpen: 'Open',
    filterRated: 'Assessed',
    note: 'When you change candidate, the current entries stay on the device and reappear when you switch back.',
    empty: 'No candidate matches the search and filter.',
    scan: 'Scan QR code',
    close: 'Close',
    state: {
      wartet: 'waiting',
      laeuft: 'in progress',
      bewertet: 'assessed',
      entschuldigt: 'excused',
    },
  },

  switchCandidate: {
    kicker: 'Change candidate',
    withDraft: (name) => `The entries for ${name} will be shown again.`,
    blank: 'A blank sheet opens and the station clock starts over.',
    kept: (name) => `The entries for ${name} stay on the device.`,
    cancel: 'Cancel',
    confirm: 'Change',
  },

  videoAnnotate: {
    play: 'Play',
    pause: 'Pause',
    save: 'Save',
    collapse: 'Collapse bars',
    expand: 'Expand bars',
    cancel: 'Cancel',
    textPlaceholder: 'Label',
    band: 'Band',
    addBand: 'Add band',
    deleteBand: 'Delete band',
    open: 'Annotate',
    notPlayable: 'This recording cannot be opened here.',
    soundOn: 'Sound on',
    soundOff: 'Sound off',
  },
  annotate: {
    title: 'Markers',
    hint: 'Pick a tool and drag on the image. Double-tap a label to change its text.',
    toolArrow: 'Arrow',
    toolEllipse: 'Circle',
    toolRect: 'Rectangle',
    toolFreehand: 'Freehand',
    toolText: 'Text',
    toolNumber: 'Number',
    toolCover: 'Cover',
    color: 'Colour',
    size: 'Size',
    sizeSmall: 'small',
    sizeMedium: 'medium',
    sizeLarge: 'large',
    undo: 'Undo',
    redo: 'Redo',
    deleteMarker: 'Delete marker',
    clearAll: 'Delete all markers',
    clearAllAsk: 'Delete all markers on this image?',
    textPrompt: 'Label',
    textPlaceholder: 'Enter text …',
    readError: 'The existing marker layer cannot be read. Editing is locked so nothing is lost.',
    markerCount: (n: number) => (n === 1 ? '1 marker' : `${n} markers`),
    close: 'Done',
    cancel: 'Cancel',
    ok: 'Apply',
  },
  comment: {
    kicker: (task) => `Comment · Task ${task}`,
    none: 'No comment yet',
    entries: (n) => (n === 1 ? '1 entry' : `${n} entries`),
    done: 'Done',
    tabText: 'Text & phrases',
    tabHandwriting: 'Handwriting',
    tabAudio: 'Audio',
    tabPhoto: 'Photo',
    tabVideo: 'Video',
    snippets: 'Phrases',
    snippetCount: (n) => `${n} phrases`,
    freeText: 'Free text',
    freeTextHint: 'Type here, or tap a phrase on the left …',
    freeTextHintOhne: 'Type here …',
    clear: 'Clear',
    handwritingTitle: 'Handwritten comment',
    handwritingHint: 'Write directly here.',
    recordReady: 'ready',
    recordRunning: 'recording',
    recordStart: 'Start recording',
    recordStop: 'Stop recording',
    noRecording: 'No recording yet.',
    recordingAt: (time) => `Recording ${time}`,
    saved: 'saved',
    noFile: 'no file yet',
    play: 'Play',
    pausePlayback: 'Pause playback',
    deleteRecording: 'Delete recording',
    takePhoto: 'Take photo',
    cameraOpening: 'Opening camera …',
    photoCount: (n) => (n === 1 ? '1 photo' : `${n} photos`),
    noPhoto: 'No photo yet.',
    photoAt: (time) => `Photo ${time}`,
    viewPhoto: 'View photo',
    deletePhoto: 'Delete photo',
    cameraUnavailable: (message) => `Camera unavailable: ${message}`,
    recordVideo: 'Record video',
    videoCount: (n) => (n === 1 ? '1 video' : `${n} videos`),
    mergingVideo: 'Joining the segments …',
    noVideo: 'No video yet.',
    videoAt: (time) => `Video ${time}`,
    playVideo: 'Play video',
    deleteVideo: 'Delete video',
    deleteKicker: 'Put away',
    deleteTitle: {
      video: 'Put this video away?',
      photo: 'Put this photo away?',
      audio: 'Put this recording away?',
      handwriting: 'Discard the handwriting?',
    },
    deleteArchived: 'The recording moves to the archive and is deleted for good after the set period.',
    deleteGone: 'The strokes are gone afterwards — they live in the form, not as a file.',
    deleteConfirm: 'Put away',
    deleteCancel: 'Cancel',
    handwritingStrokes: (n: number) => (n === 1 ? '1 stroke' : `${n} strokes`),
    micDenied: 'Recording is not possible without microphone access.',
    close: 'Close',
  },

  lock: {
    kicker: 'Protected',
    title: 'Settings',
    hint: 'This app’s settings are protected by a password. It lives in the iPad settings under “fairexa Stage”.',
    password: 'Password',
    open: 'Open',
    wrong: 'That password is not right.',
  },

  pruefungsPasswort: {
    kicker: 'Exam password',
    laden: 'Download',
    loeschen: 'Delete',
    hint: 'This exam is protected by a password. The exam supervisors know it.',
    password: 'Password',
    weiter: 'Continue',
    wrong: 'That password is not right.',
  },

  exit: {
    kicker: 'Exam client',
    title: 'Quit',
    hint: 'Enter the PIN to quit. It lives in the device settings under “fairexa Stage”.',
    pin: 'PIN',
    quit: 'Quit',
    cancel: 'Keep assessing',
    wrong: 'That PIN is not right.',
    button: 'Quit',
    menu: 'Quit exam …',
    menuFile: 'File',
    menuEdit: 'Edit',
    menuView: 'View',
  },

  vignette: {
    situation: 'Situation',
    task: 'Task',
    instructions: 'Examiner instructions',
    material: 'Material',
    materialMissing: 'Not on this device',
  },

  clock: {
    ahead: 'fast',
    behind: 'slow',
    hour: 'hour',
    hours: 'hours',
    minute: 'minute',
    minutes: 'minutes',
    seconds: 'seconds',
  },
  settings: {
    groupDevice: 'This device',
    deviceName: 'Name',
    deviceNamePlaceholder: 'e.g. Exam device 3',
    deviceNameNote: 'The name goes into every signature and will appear next to every recording from this device.',
    deviceId: 'Identifier',
    clockOffTitle: 'This device\'s clock is wrong',
    clockOffBody: (abweichung: string) =>
      `It differs from network time by ${abweichung}. Timestamps from this device are wrong.`,
    clockOffFix: 'Correct it in the device settings under "Date & Time".',
    clockOffClose: 'Understood',
    title: 'Appearance and sheet',
    kicker: 'Settings',
    accent: 'Accent colour',
    language: 'Language',
    managedByMdm: 'Set by device management — not editable here.',
    passwortStand: (gesetzt) =>
      gesetzt ? 'A password is set.' : 'No password set – the settings are open to everyone.',
    passwortNeu: 'New password',
    passwortWiederholen: 'Repeat',
    passwortSetzen: 'Set password',
    passwortEntfernen: 'Remove password',
    passwortUngleich: 'The two entries do not match.',
    passwortGesetzt: 'Set. It applies as soon as the app goes to the background or the device sleeps.',
    passwortEntfernt: 'Removed.',
    passwortAusQr: 'Set by a QR code — not editable here.',
    brightness: 'Brightness',
    brightnessFixed: 'Set by the supervisor',
    languageNote: 'Applies to the interface only. Exam content stays as it was entered.',
    erklaerung: 'Explanation',
    ausPruefung: 'Set by the exam',
    inPruefungGeaendert: 'Changed for this exam — applies until the exam changes',
    groupSheet: 'Sheet',
    halfPoints: 'Allow half points',
    halfPointsNote: 'The score sheet then offers 0.5 steps.',
    showTimer: 'Show time left in the header',
    showTimerNote: 'Tap it to start, pause and reset.',
    groupRecording: 'Recording',
    videoMaxNote: 'Upper limit for one video, across all segments together.',
    minutes: (n: number) => (n === 1 ? '1 minute' : `${n} minutes`),
    groupArchive: 'Archive of deleted exams',
    archiveNote: 'How long recordings of a deleted exam stay on the device.',
    months: (n: number) => (n === 1 ? '1 month' : `${n} months`),
    groupAfterSubmit: 'After submitting',
    scanner: 'Open the QR scanner afterwards',
    scannerNote: 'Otherwise the next candidate is chosen from the list.',
    haptics: 'Haptic feedback',
    hapticsNote: 'A short response on every tap on a checklist row.',
    groupTrial: 'Trial run',
  },

  server: {
    group: 'Server',
    url: 'Address',
    urlPlaceholder: 'https://relay.example:8111',
    login: 'User name',
    password: 'Password',
    save: 'Save and test',
    testing: 'Testing …',
    connected: (zeit: string) => `Access works. Server time ${zeit}.`,
    connectedOhneZeit: 'Access works. This server reports no time — the offset stays unmeasured.',
    drift: (sekunden: number) => `Careful: this device's clock differs from the server by ${sekunden} s.`,
    failed: (grund: string) => `No connection: ${grund}`,
    none: 'No server configured — the app works on this device alone.',
    sendNow: 'Send now',
    sent: (n: number) => `${n} sent`,
    downloadOhnePunkte:
      'The exam arrived without any scores — the server sends neither scale steps nor points for its answer rows. The sheet shows them as a checklist worth one point each. Please tell the exam lead before marking with it.',
    mode: 'Protocol',
    messageFromServer: (text: string) => `Message from the server: ${text}`,
    sendStuck: (n: number) =>
      n === 1
        ? '1 entry cannot be transmitted — see the log for details.'
        : `${n} entries cannot be transmitted — see the log for details.`,
    passwordVolatile: 'The password could not be stored permanently (keychain). It applies to this session only.',
    connect: 'Test access',
  },

  submit: {
    kicker: 'Submit assessment',
    noComment: '—',
    totalLabel: 'Total',
    openTasks: (names, count) =>
      count === 1
        ? `One task has not been assessed: ${names}.`
        : `${count} tasks have not been assessed: ${names}.`,
    screenshotNote: 'A screenshot of the sheet is saved and linked to the candidate. ',
    cancel: 'Cancel',
    confirm: 'Submit',
  },

  locked: {
    kicker: 'Submitted',
    title: 'Assessment submitted',
    meta: (score, max, at) => `${score} / ${max} points · ${at}`,
    metaOhnePunkte: (at) => at,
    note: 'The sheet is locked and stays visible for checking.',
    next: (name) => `Next: ${name}`,
    scanNext: 'Scan next candidate',
    fromList: 'Next from list',
  },

  belege: {
    group: 'Records',
    holen: 'Save records as ZIP',
    laeuft: 'Packing …',
    hinweis:
      'Recordings, sheet snapshots and attestations held by this browser — including the archive of deleted exams. On the iPad the same files are available through Finder.',
    leer: 'There are no records.',
    fertig: (dateien: number, mb: number) =>
      dateien === 1 ? `1 file saved (${mb} MB).` : `${dateien} files saved (${mb} MB).`,
    fehler: 'Saving failed:',
  },

  persistence: {
    resumed: 'State restored',
    writeFailed: 'Could not be saved — please notify the exam supervisor',
    deleteFailed: 'Could not delete the file — entry stays removed',
    startupFailed:
      'The exam could not be loaded. Please restart the app or notify the exam supervisor.',
    demoInstalled: 'Demo exam installed',
    pfadZuLang:
      'The evidence folder is nested too deeply — recordings may not be saved. Please inform the exam supervisor.',
  },

  sleep: {
    sleep: 'Dim the screen',
    wake: 'Wake up',
    wakeHint: 'Tap here to carry on',
  },

  scanner: {
    title: 'Scan the next candidate',
    window: 'Time window',
    hint: 'Hold the candidate’s QR code inside the frame',
    unknown: (code) => `No candidate for this card: ${code}`,
    einrichtungscode: 'This is a setup code — please scan it in the setup sheet, not here.',
    cancel: 'Cancel',
    scan: 'Scan QR code',
    noCamera: 'Without a camera: next candidate',
    ariaLabel: 'Scan card',
    timeoutTitle: 'No card detected',
    timeoutText: (seconds) =>
      `After ${seconds} seconds without a scan: sign the candidate out and release the station, or go back to the last sheet.`,
    back: 'Back to the sheet',
    logout: 'Sign candidate out',
  },
};
